import {
  dateKeyToDate,
  extractEventCategories,
  getEventDateRange,
  normalize,
  startOfMonth,
  toDateKey,
} from "./calendarUtils";

export function toMonthKey(dateInput) {
  const dateKey = toDateKey(dateInput);

  if (!dateKey) return null;

  return dateKey.slice(0, 7);
}

export function monthKeyToDate(monthKey) {
  if (!monthKey) return null;

  const [year, month] = String(
    monthKey
  )
    .split("-")
    .map(Number);

  if (!year || !month) return null;

  return new Date(
    year,
    month - 1,
    1,
    12,
    0,
    0,
    0
  );
}

export function formatMonthOptionLabel(
  monthKey
) {
  const date =
    monthKeyToDate(monthKey);

  if (!date) return monthKey;

  const label =
    new Intl.DateTimeFormat(
      "fr-FR",
      {
        month: "long",
        year: "numeric",
      }
    ).format(date);

  return (
    label.charAt(0).toUpperCase() +
    label.slice(1)
  );
}

/**
 * Renvoie les mois contenant au moins
 * un événement.
 *
 * Un événement couvrant deux mois apparaît
 * dans les deux mois.
 */
export function getMonthOptions(
  events = []
) {
  const monthMap = new Map();

  events.forEach((event) => {
    const { startKey, endKey } =
      getEventDateRange(event);

    if (!startKey || !endKey) {
      return;
    }

    const startDate =
      dateKeyToDate(startKey);

    const endDate =
      dateKeyToDate(endKey);

    if (!startDate || !endDate) {
      return;
    }

    let cursor =
      startOfMonth(startDate);

    const lastMonth =
      startOfMonth(endDate);

    while (cursor <= lastMonth) {
      const monthKey =
        toMonthKey(cursor);

      if (!monthMap.has(monthKey)) {
        monthMap.set(
          monthKey,
          new Set()
        );
      }

      monthMap
        .get(monthKey)
        .add(
          event.id ??
            `${event.titre}-${event.start}`
        );

      cursor = new Date(
        cursor.getFullYear(),
        cursor.getMonth() + 1,
        1,
        12,
        0,
        0,
        0
      );
    }
  });

  return Array.from(
    monthMap.entries()
  )
    .sort(([firstKey], [secondKey]) =>
      firstKey.localeCompare(secondKey)
    )
    .map(([value, eventIds]) => ({
      value,
      label:
        formatMonthOptionLabel(value),
      count: eventIds.size,
    }));
}

export function getEventPublicStatus(
  event = {}
) {
  const rawStatus = normalize(
    event.status_event ||
      event.statut_event ||
      event.event_status ||
      event.status ||
      ""
  );

  if (
    rawStatus.includes("annule") ||
    rawStatus.includes("cancelled") ||
    rawStatus.includes("canceled")
  ) {
    return {
      key: "cancelled",
      label: "Annulé",
    };
  }

  if (
    rawStatus.includes("reporte") ||
    rawStatus.includes("postponed")
  ) {
    return {
      key: "postponed",
      label: "Reporté",
    };
  }

  if (
    rawStatus.includes("complet") ||
    rawStatus.includes("full")
  ) {
    return {
      key: "full",
      label: "Complet",
    };
  }

  if (
    rawStatus.includes(
      "provisoire"
    ) ||
    rawStatus.includes("draft")
  ) {
    return {
      key: "provisional",
      label: "Provisoire",
    };
  }

  return null;
}

export function getGoogleMapsUrl(
  location
) {
  if (!location) return null;

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    location
  )}`;
}

function escapeIcsText(value = "") {
  return String(value)
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

function formatIcsUtcDate(dateInput) {
  const date = new Date(dateInput);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}Z$/, "Z");
}

function formatIcsAllDayDate(
  dateInput
) {
  const dateKey = toDateKey(dateInput);

  return dateKey
    ? dateKey.replace(/-/g, "")
    : null;
}

function isDateOnlyValue(value) {
  if (!value) return false;

  return /^\d{4}-\d{2}-\d{2}$/.test(
    String(value).trim()
  );
}

function addOneDayToDateKey(
  dateInput
) {
  const dateKey =
    toDateKey(dateInput);

  const date =
    dateKeyToDate(dateKey);

  if (!date) return null;

  date.setDate(date.getDate() + 1);

  return formatIcsAllDayDate(date);
}

function stripHtml(value = "") {
  if (!value) return "";

  if (
    typeof document !== "undefined"
  ) {
    const container =
      document.createElement("div");

    container.innerHTML =
      String(value);

    return (
      container.textContent ||
      container.innerText ||
      ""
    ).trim();
  }

  return String(value)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function downloadEventIcs(
  event = {}
) {
  if (!event.start) return;

  const isAllDay =
    isDateOnlyValue(event.start) &&
    (!event.end ||
      isDateOnlyValue(event.end));

  let dateLines = [];

  if (isAllDay) {
    const startDate =
      formatIcsAllDayDate(
        event.start
      );

    const endDate =
      addOneDayToDateKey(
        event.end || event.start
      );

    dateLines = [
      `DTSTART;VALUE=DATE:${startDate}`,
      `DTEND;VALUE=DATE:${endDate}`,
    ];
  } else {
    const startDate =
      formatIcsUtcDate(
        event.start
      );

    const fallbackEnd =
      new Date(event.start);

    fallbackEnd.setHours(
      fallbackEnd.getHours() + 2
    );

    const endDate =
      formatIcsUtcDate(
        event.end || fallbackEnd
      );

    dateLines = [
      `DTSTART:${startDate}`,
      `DTEND:${endDate}`,
    ];
  }

  const categories =
    extractEventCategories(
      event.cate_event
    );

  const informationLines = [
    event.discipline_event
      ? `Discipline : ${event.discipline_event}`
      : "",

    event.type_event
      ? `Type : ${event.type_event}`
      : "",

    event.level_event
      ? `Niveau : ${event.level_event}`
      : "",

    categories.length > 0
      ? `Catégories : ${categories.join(
          ", "
        )}`
      : "",

    event.organisateur
      ? `Organisateur : ${event.organisateur}`
      : "",

    event.horaire_event
      ? `Horaires : ${event.horaire_event}`
      : "",
  ].filter(Boolean);

  const description = [
    informationLines.join("\n"),

    event.description
      ? stripHtml(event.description)
      : "",

    event.agenda_url
      ? `Document : ${event.agenda_url}`
      : "",

    event.suivi_event
      ? `Suivi compétition : ${event.suivi_event}`
      : "",
  ]
    .filter(Boolean)
    .join("\n\n");

  const uid = `${
    event.id || Date.now()
  }-${Date.now()}@ligue-judo-nouvelle-aquitaine`;

  const icsContent = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Ligue Judo Nouvelle-Aquitaine//Calendrier//FR",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${formatIcsUtcDate(
      new Date()
    )}`,
    ...dateLines,
    `SUMMARY:${escapeIcsText(
      event.titre || "Événement"
    )}`,
    `LOCATION:${escapeIcsText(
      event.lieu_event || ""
    )}`,
    `DESCRIPTION:${escapeIcsText(
      description
    )}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");

  const blob = new Blob(
    [icsContent],
    {
      type: "text/calendar;charset=utf-8",
    }
  );

  const url =
    URL.createObjectURL(blob);

  const link =
    document.createElement("a");

  const safeFilename = String(
    event.titre || "evenement"
  )
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(
      /[^a-zA-Z0-9-_]+/g,
      "-"
    )
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();

  link.href = url;

  link.download = `${
    safeFilename || "evenement"
  }.ics`;

  document.body.appendChild(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
}